import { Injectable } from '@angular/core';
import {
    combineQueries,
    distinctUntilArrayItemChanged,
    QueryEntity
} from '@datorama/akita';
import { PositionsStore, PositionsState } from './positions.store';
import {
    checkFilter,
    flattenNestedObject,
    InferFilter
} from '../../../shared/state/query-filter.functions';
import { get } from 'dot-prop';
import {
    AccumulatedPosition,
    AccumulatedPositionWithWeights,
    ExtendedPosition,
    extendPosition,
    Position
} from './position.model';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take
} from 'rxjs/operators';
import { AccountTypeEnum } from '../../../shared/state/accts/acct.model';
import { AcctsQuery } from '../../../shared/state/accts/accts.query';
import { combineLatest, Observable, Subject } from 'rxjs';
import { Instrument } from '../../../shared/state/instruments/instrument.model';
import { getTotals } from './positions.accumulators';
import { SettingsQuery } from '../../settings/state/settings.query';
import { PositionsService } from './positions.service';
import { TransactionsQuery } from '../../transactions/state/transactions.query';
import { TimeSeriesDataQuery } from '../../time-series-data/state/time-series-data.query';
import { UserSettingsQuery } from '../../user-settings/state/user-settings.query';
import { AccountIssuersQuery } from '../../../pages/admin/state/account-issuers/account-issuers.query';
import { LegalEntityQuery } from '../../../pages/admin/state/legal-entities/legal-entity.query';
import { InstrumentsQuery } from '../../../shared/state/instruments/instruments.query';
import { GroupBy } from '../../group-by/state/group-by.model';
import { accumulateByGroup, accumulateByGroupV2 } from '../../aggregation/aggregation';

@Injectable({ providedIn: 'root' })
export class PositionsQuery extends QueryEntity<PositionsState> {
    private readonly _refresh$ = new Subject<Date>();
    constructor(
        protected store: PositionsStore,
        private _accountsQuery: AcctsQuery,
        private _accountIssuerQuery: AccountIssuersQuery,
        private _legalEntityQuery: LegalEntityQuery,
        private _instrumentsQuery: InstrumentsQuery,
        private _settingsQuery: SettingsQuery,
        private _positionsService: PositionsService,
        private _transactionsQuery: TransactionsQuery,
        private _timeSeriesDataQuery: TimeSeriesDataQuery,
        private _userSettingsQuery: UserSettingsQuery
    ) {
        super(store);

        /** @description When positions query is loaded by a component we fetch all positions by default and set up a subscription to update
         * positions whenever one of the "parameters" or "inputs" emits.
         * @param transactionEntry - Emits whenever transactions are added, updated or removed with a debounce of 5000 ms
         * @param timeSeriesEntry - Emits whenever time series data (price data) is added, updated or removed with a debounce of 3000 ms
         * @param accountIds - Emits whenever accounts are added or removed (except broker accounts)
         * @param baseCurrency - Emits whenever the base currency is changed
         * @param refresh - Emits whenever the refresh method is called with a debounce of 10000 ms to prevent spamming the backend
         * */
        combineQueries([
            this._transactionsQuery.lastDataEntryByUser$.pipe(
                debounceTime(5000),
                startWith(null)
            ),
            this._timeSeriesDataQuery.lastDataEntryByUser$.pipe(
                debounceTime(3000),
                startWith(null)
            ),
            this._accountsQuery.isLoading$.pipe(
                filter((isLoading) => !isLoading), // wait until accounts are loaded
                take(1), // only wait for the first load
                switchMap(() =>
                    // switch to the next observable (all accounts except broker accounts)
                    this._accountsQuery.selectAll({
                        filterBy: (account) =>
                            account.accountType !== AccountTypeEnum.BROKER
                    })
                ),
                map((accounts) => accounts.map((account) => account.accountId)),
                distinctUntilArrayItemChanged()
            ),
            this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY'),
            this._refresh$.pipe(debounceTime(5000), startWith(new Date()))
        ])
            .pipe(
                switchMap(
                    ([
                        transactionsStoreUpdate,
                        timeSeriesStoreUpdate,
                        accountIds,
                        baseCurrency,
                        refreshEvent
                    ]) =>
                        this._positionsService.get({
                            accountIds,
                            persistToStore: true,
                            includeNetted: true,
                            baseCurrency
                        })
                )
            )
            .subscribe();
    }

    /** refresh - Method to refresh the positions query "manually" */
    refresh() {
        this._positionsService.bustCache();
        this._refresh$.next(new Date());
    }

    /** isLoading$ - Observable for when the positions in the store are loading */
    isLoading$ = this.selectLoading();

    /** updatedAt$ - Observable for when the positions in the store were updated  */
    updatedAt$ = this.select((state) => state.ui.updatedAt);

    selectAllByAccount$(groupOnParentAccount = true, filter: InferFilter<Position> = {}) {
        return combineLatest([
            this.selectPositionsByFilter$(filter),
            this._accountsQuery.accountsAsObject$.pipe(distinctUntilChanged())
        ]).pipe(
            map(([positions, accountMap]) => {
                const accounts = {};
                for (let i = 0; i < positions.length; i++) {
                    const position = positions[i];
                    const positionAccount = accountMap[position.account.accountId];
                    let accountId = position.account.accountId;
                    const balance = position.marketValue ?? 0;
                    const balanceInBaseCurrency = position.marketValueInBaseCurrency ?? 0;

                    // only check if position is a sub-account if groupOnParentAccount is true
                    const isSubAccount =
                        groupOnParentAccount && positionAccount.parentAccountId !== null;

                    // if position is a sub-account...
                    if (isSubAccount) {
                        // ...use parent account ID as account id to accumulate positions on parent account level
                        accountId = positionAccount.parentAccountId;
                    }
                    if (!accounts[accountId]) {
                        accounts[accountId] = {
                            positions: [],
                            balance: 0,
                            balanceInBaseCurrency: 0
                        };
                        if (isSubAccount) {
                            accounts[accountId].subAccounts = [
                                position.account.accountId
                            ];
                        }
                    }

                    if (
                        isSubAccount &&
                        !accounts[accountId].subAccounts?.includes(
                            position.account.accountId
                        )
                    ) {
                        accounts[accountId].subAccounts?.push(position.account.accountId);
                    }
                    accounts[accountId].positions.push(position);
                    accounts[accountId].balance += balance;
                    accounts[accountId].balanceInBaseCurrency += balanceInBaseCurrency;
                }
                return accounts;
            })
        );
    }

    selectPositionsByFilter$(filters: InferFilter<Position> = {}) {
        return this.selectAll({
            filterBy: (position) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(position, key);
                        return !checkFilter(value, criteria, position);
                    });
                } else {
                    return true;
                }
            }
        });
    }

    /** selectActivePositionsByFilter$
     * @description Observable for all positions on active legal entity, by default broker accounts are excluded
     * @param filters
     * @returns Observable<Array<Position>>
     */
    selectActivePositionsByFilter$(filters: InferFilter<Position> = {}) {
        return this._accountsQuery.selectActiveAccounts$.pipe(
            switchMap((accounts) => {
                return this.selectPositionsByFilter$({
                    ...filters,
                    account: {
                        accountType: { nin: [AccountTypeEnum.BROKER] },
                        ...(filters.account || {}),
                        accountId: { in: accounts.map((a) => a.accountId) }
                    }
                });
            })
        );
    }

    /** selectActivePositions$
     * @description Observable for all positions on active legal entity, by default broker accounts are excluded
     * @returns Observable<Array<Position>>
     */
    selectActivePositions$ = this._accountsQuery.selectActiveAccounts$.pipe(
        switchMap((accounts) => {
            return this.selectPositionsByFilter$({
                account: {
                    accountId: { in: accounts.map((a) => a.accountId) },
                    accountType: { nin: [AccountTypeEnum.BROKER] }
                }
            });
        }),
        shareReplay(1)
    );

    /** selectPositionsByInstrumentType$
     * @description Observable for all positions grouped by instrument type
     * @returns Observable<Array<Instrument['instrumentId']>>
     */
    selectPositionsByInstrumentType$(filter: InferFilter<Position> = {}) {
        return this.selectPositionsByFilter$(filter).pipe(
            map((positions) => {
                const groups = {};
                for (let position of positions) {
                    if (groups[position.instrument.type]) {
                        groups[position.instrument.type].push(position);
                    } else {
                        groups[position.instrument.type] = [position];
                    }
                }
                return groups;
            }),
            shareReplay(1)
        );
    }

    /** selectInstrumentIdsWithPositions$
     * @description Observable for the instrument ids that have positions
     * @param filter - optional filter to apply to the positions
     * @returns Observable<Array<Instrument['instrumentId']>>
     */
    selectInstrumentIdsWithPositions$ = (
        filter: InferFilter<Position> = {
            marketValueInBaseCurrency: {
                fn: (value, object) => {
                    // filter out positions that are considered netted
                    return value > 0.001 || value < -0.001;
                }
            }
        }
    ): Observable<Array<Instrument['instrumentId']>> => {
        return this.selectPositionsByFilter$(filter).pipe(
            map((positions) => {
                return Array.from(
                    new Set(positions.map((position) => position.instrument.instrumentId))
                );
            }),
            distinctUntilArrayItemChanged(),
            shareReplay(1)
        );
    };

    // TODO: investigate why it differs from API response in demo
    selectTotals$ = (filters: InferFilter<Position> = {}) => {
        return this._accountsQuery
            .selectAccountsByFilter$({
                accountType: {
                    nin: [AccountTypeEnum.BROKER]
                },
                parentAccountId: { eq: null }
            })
            .pipe(
                map((accounts) => accounts.map((account) => account.accountId)),
                distinctUntilArrayItemChanged(),
                switchMap((accountIds) =>
                    combineQueries([
                        this.selectPositionsByFilter$({
                            ...filters,
                            account: {
                                accountId: { in: accountIds }
                            }
                        }),
                        this._settingsQuery.currenciesAndRates$
                    ]).pipe(
                        map(([positions, currenciesAndRates]) =>
                            getTotals(positions, currenciesAndRates)
                        )
                    )
                ),
                shareReplay(1)
            );
    };

    selectActiveTotalsByFilter$ = (filters: InferFilter<Position> = {}) => {
        return this._accountsQuery.selectActiveAccounts$.pipe(
            switchMap((accounts) => {
                return combineQueries([
                    this.selectPositionsByFilter$({
                        account: {
                            accountType: { nin: [AccountTypeEnum.BROKER] },
                            ...(filters.account || {}),
                            accountId: { in: accounts.map((a) => a.accountId) }
                        },
                        ...filters
                    }),
                    this._settingsQuery.currenciesAndRates$
                ]).pipe(
                    map(([positions, currenciesAndRates]) =>
                        getTotals(positions, currenciesAndRates)
                    )
                );
            })
        );
    };

    selectExtendedPositionsByFilter$(filters: InferFilter<Position> = {}) {
        return combineQueries([
            this.selectPositionsByFilter$(filters),
            this._settingsQuery.currenciesAndRates$,
            this._accountsQuery.isLoading$.pipe(
                filter((isLoading) => isLoading === false),
                take(1)
            ),
            this._accountIssuerQuery.isLoading$.pipe(
                filter((isLoading) => isLoading === false),
                take(1)
            ),
            this._legalEntityQuery.isLoading$.pipe(
                filter((isLoading) => isLoading === false),
                take(1)
            )
        ]).pipe(
            map(([positions, currenciesAndRates]) => {
                const extendedPositions: Array<ExtendedPosition> = [];
                for (let i = 0; i < positions.length; i++) {
                    const position = positions[i];
                    const account = this._accountsQuery.getAccountById(
                        position.account.accountId
                    );
                    const accountIssuer = this._accountIssuerQuery.getIssuerById(
                        account.accountIssuerId
                    );
                    const legalEntity = this._legalEntityQuery.getOwnerById(
                        account.ownerId
                    );
                    const instrument = this._instrumentsQuery.getInstrumentById(
                        position.instrument.instrumentId
                    );
                    const extendedPosition = extendPosition(
                        position,
                        account,
                        accountIssuer,
                        legalEntity,
                        instrument,
                        currenciesAndRates
                    );
                    extendedPositions.push(extendedPosition);
                }
                return extendedPositions;
            })
        );
    }

    /** selectPositionsByGroups$ - Observable for positions grouped by groups
     * @deprecated Use selectPositionsByGroupsV2$ instead
     * @description This method is used to group positions by the provided groups and accumulate the values of the positions
     * @param groups - Array of GroupBy objects to group the positions by
     * @param filters - Optional filters to apply to the positions
     * @returns Observable<Array<AccumulatedPosition>>
     */
    selectPositionsByGroups$(
        groups: Array<GroupBy>,
        filters: InferFilter<Position> = {}
    ) {
        return this.selectExtendedPositionsByFilter$({
            ...filters
        }).pipe(
            map((positions) => {
                return accumulateByGroup<
                    ExtendedPosition,
                    {
                        marketValueInBaseCurrency: number;
                        acquisitionValueInBaseCurrency: number;
                        totalPnlInBaseCurrency: number;
                    }
                >(
                    positions,
                    (position, acc, index, subgroup) => {
                        const marketValueInBaseCurrency =
                            (acc?.marketValueInBaseCurrency ?? 0) +
                            position.marketValueInBaseCurrency;
                        const acquisitionValueInBaseCurrency =
                            (acc?.acquisitionValueInBaseCurrency ?? 0) +
                            position.acquisitionValueInBaseCurrency;
                        const totalPnlInBaseCurrency =
                            (acc?.totalPnlInBaseCurrency ?? 0) +
                            position.totalPnlInBaseCurrency;
                        return {
                            marketValueInBaseCurrency,
                            acquisitionValueInBaseCurrency,
                            totalPnlInBaseCurrency
                        };
                    },
                    groups
                );
            })
        );
    }

    /** includeNetted$ - Observable for the includeNetted property in the positions store
     * @description This property is used to determine if positions that are zero (sold off) should be included in the positions
     * @returns Observable<boolean>
     */
    includeNetted$ = this.select((state) => state.ui.includeNetted).pipe(shareReplay(1));

    /** includeNetted - Getter for the includeNetted property in the positions store
     * @description This property is used to determine if positions that are zero (sold off) should be included in the positions
     * @returns boolean
     */
    get includeNetted() {
        return this.getValue().ui.includeNetted;
    }

    /** selectPositionsByGroupsV2$ - Observable for positions grouped by groups
     * @description This method is used to group positions by the provided groups and accumulate the values of the positions
     * @param groups - Array of GroupBy objects to group the positions by
     * @param filters - Optional filters to apply to the positions
     * @returns Observable<{entities: Array<ExtendedPosition>, groups: { [key: string]: AccumulatedPositionWithWeights }}>
     */
    selectPositionsByGroupsV2$(
        groups: Array<GroupBy>,
        filters: InferFilter<Position> = {}
    ) {
        return this.selectExtendedPositionsByFilter$({
            ...filters
        }).pipe(
            map((positions) => {
                return {
                    entities: positions,
                    groups: {
                        ...accumulateByGroupV2<ExtendedPosition, AccumulatedPosition>(
                            positions,
                            (entity, acc, index, subgroup, total) => {
                                //console.log(total);
                                const totalPnlInBaseCurrency =
                                    (acc?.totalPnlInBaseCurrency ?? 0) +
                                    entity.totalPnlInBaseCurrency;
                                const totalCommission =
                                    (acc?.totalCommission ?? 0) +
                                    entity.position?.totalCommission;
                                const totalDividend =
                                    (acc?.totalDividend ?? 0) +
                                    entity.position?.totalDividend;
                                const realizedQuantity =
                                    (acc?.realizedQuantity ?? 0) +
                                    entity.position?.realizedQuantity;
                                const marketValueInBaseCurrency =
                                    (acc?.marketValueInBaseCurrency ?? 0) +
                                    entity.marketValueInBaseCurrency;
                                const acquisitionValueInBaseCurrency =
                                    (acc?.acquisitionValueInBaseCurrency ?? 0) +
                                    entity.acquisitionValueInBaseCurrency;
                                const cashInBaseCurrency =
                                    (acc?.cashInBaseCurrency ?? 0) +
                                    entity.cashInBaseCurrency;
                                const marketValueAllocation =
                                    total?.marketValueInBaseCurrency
                                        ? marketValueInBaseCurrency /
                                          total.marketValueInBaseCurrency
                                        : 0;

                                return {
                                    totalPnlInBaseCurrency,
                                    totalCommission,
                                    totalDividend,
                                    realizedQuantity,
                                    marketValueInBaseCurrency,
                                    acquisitionValueInBaseCurrency,
                                    cashInBaseCurrency,
                                    marketValueAllocation
                                };
                            },
                            groups
                        )
                    } as {
                        [key: string]: AccumulatedPositionWithWeights;
                    }
                };
            }),
            shareReplay(1)
        );
    }
}
